import {ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {firstValueFrom, of, Subject} from 'rxjs';
import {catchError, finalize, map, switchMap, take, takeUntil} from 'rxjs/operators';
import {FieldFilter} from '@6grain/masofa-monolith-api-client/dictionaries';
import {FieldService} from '@6grain/masofa-monolith-api-client/crop-monitoring';
import {BidService} from '@6grain/masofa-monolith-api-client/crop-monitoring/api/bid.service';
import {AnomalyPolygonsService} from '@6grain/masofa-monolith-api-client/satellite-indices';
import {ListPageWrapper} from '@app/wrappers/pages/list-page-wrapper/list-page-wrapper';
import {BackButton} from '@components/ui/back-button/back-button';
import {Filters} from '@components/views/filters/filters';
import {IMsfFormBuilder} from '@components/form-builder/form-builder';
import {RegionManagerService} from '@components/openstreetmap/managers/region-manager.service';
import {buildFilterQuery, replaceFilter} from '@app/utils/_msf';
import {FilterOperator} from '@app/utils/filter-operator';
import {ButtonModule} from 'primeng/button';
import {DialogModule} from 'primeng/dialog';
import {MenuModule} from 'primeng/menu';
import {MenuItem} from 'primeng/api';
import {
  AnomaliesList
} from '@app/pages/electronic-field-map/pages/gis-fields/components/modals/anomalies-dialog/anomalies-list/anomalies-list';
import {GisAnomaliesComponent} from './gis-anomalies/gis-anomalies';
import {ANOMALY_LEGEND_ITEMS} from './anomaly-type-colors';
import {withDefaultAnomalyArchiveFilter} from './anomalies-archive-filter.util';
import {
  ANOMALIES_MAP_BID_NUMBER_QUERY_KEY,
  anomalyIdsFromCustomGetRows,
  anomalyPolygonFilterForLinkedBid,
  buildLinkedBidNumberFilterField,
  collectLinkedAnomalyIds,
  createBidNumberEqualsQuery,
  createPolygonsByBidIdQuery,
  emptyAnomalyMatchFilter,
  parseLinkedBidNumber,
  pickFirstBid,
  readLinkedBidNumberFromQuery,
} from './anomalies-linked-bid-filter.util';

@Component({
  selector: 'app-anomalies-map-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ListPageWrapper,
    BackButton,
    Filters,
    ButtonModule,
    DialogModule,
    MenuModule,
    AnomaliesList,
    GisAnomaliesComponent,
  ],
  templateUrl: './anomalies-map-page.html',
  styleUrl: './anomalies-map-page.scss',
})
export class AnomaliesMapPageComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @ViewChild(GisAnomaliesComponent) gisAnomalies?: GisAnomaliesComponent;

  viewTab: 'board' | 'map' = 'board';
  private pendingFocusAnomalyId: string | null = null;
  /** Last bid number taken from / written to the query; `null` means “show all”. */
  private lastQueryLinkedBidNumber: number | null = null;
  isSmallScreen = false;
  isFilterModalOpen = false;
  loading = false;

  filters: Record<string, any> = {AnomalyOrigin: 'Satellite'};
  filtersParams: FieldFilter[] = withDefaultAnomalyArchiveFilter([
    {
      filterField: 'AnomalyOrigin',
      filterOperator: FilterOperator.Equals as any,
      filterValue: 'Satellite',
    },
  ]);
  anomalyFilterFields: IMsfFormBuilder[] = [];
  toggleFilter = false;
  exportLoading = false;
  exportMenuItems: MenuItem[] = [];

  flexRowSizes = {left: '9', right: '3'};

  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    public router: Router,
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
    public translate: TranslateService,
    private readonly regionManagerService: RegionManagerService,
    public fieldService: FieldService,
    private readonly anomalyPolygonsService: AnomalyPolygonsService,
    private readonly bidService: BidService,
  ) {}

  ngOnInit(): void {
    this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.Tablet]).pipe(takeUntil(this.destroy$)).subscribe(r => {
      this.isSmallScreen = r.matches;
      this.flexRowSizes = this.isSmallScreen ? {left: '12', right: '0'} : {left: '9', right: '3'};
    });

    this.anomalyFilterFields = [
      {
        title: 'Region',
        name: 'regionId',
        type: 'tree-select',
        selectService: this.regionManagerService,
        selectServiceMethod: 'getRegionsObservable',
        options: {
          treeSelect: {
            treeBy: 'parentId',
          },
        },
        placeholder: 'Select region',
      },
      {
        name: 'fieldId',
        title: 'Field name',
        type: 'select-paginate',
        selectService: this.fieldService,
        selectServiceMethod: 'cropMonitoringFieldGetByQueryPost',
        placeholder: 'Enter field name',
        options: {
          select: {
            optionLabel: 'name',
            optionValue: 'id',
            searchBy: 'name',
            translateBy: 'none',
          },
        },
      },
      {
        name: 'AnomalyOrigin',
        title: 'ANOMALIES_FILTER_ANOMALY_ORIGIN',
        type: 'select',
        placeholder: 'ANOMALIES_FILTER_ANOMALY_ORIGIN_PLACEHOLDER',
        selectOptions: [
          {name: 'ANOMALY_ORIGIN_SATELLITE', id: 'Satellite'},
          {name: 'ANOMALY_ORIGIN_WORKER', id: 'Worker'},
          {name: 'ANOMALY_ORIGIN_BOTH', id: 'Both'},
        ],
        options: {
          clearable: true,
          select: {optionLabel: 'name', optionValue: 'id', translateBy: 'locale'},
        },
      },
      {
        name: 'productSourceType',
        title: 'ANOMALIES_FILTER_PRODUCT_SOURCE',
        type: 'select',
        placeholder: 'ANOMALIES_FILTER_PRODUCT_SOURCE_PLACEHOLDER',
        disabled: () => this.isProductSourceFilterDisabled(),
        selectOptions: [
          {name: 'PRODUCT_SOURCE_TYPE_CUSTOM', id: 0},
          {name: 'PRODUCT_SOURCE_TYPE_SENTINEL2', id: 1},
          {name: 'PRODUCT_SOURCE_TYPE_LANDSAT', id: 2},
        ],
        options: {
          clearable: true,
          select: {optionLabel: 'name', optionValue: 'id', translateBy: 'locale'},
        },
      },
      {
        name: 'anomalyType',
        title: 'ANOMALIES_FILTER_ANOMALY_TYPE',
        type: 'select',
        placeholder: 'ANOMALIES_FILTER_ANOMALY_TYPE_PLACEHOLDER',
        selectOptions: ANOMALY_LEGEND_ITEMS.map(({anomalyType, labelKey}) => ({
          name: labelKey,
          id: anomalyType,
        })),
        options: {
          clearable: true,
          select: {optionLabel: 'name', optionValue: 'id', translateBy: 'locale'},
        },
      },
      {
        name: 'originalDate',
        title: 'Detection date',
        type: 'date',
        placeholder: 'Select period',
        options: {
          date: {
            selectionMode: 'range',
          },
        },
      },
      buildLinkedBidNumberFilterField(),
    ];

    this.exportMenuItems = [
      {
        label: this.translate.instant('Export') + ' Excel',
        icon: 'pi pi-file-excel',
        command: () => this.exportAnomalies('excel'),
      },
      {
        label: this.translate.instant('Export') + ' CSV',
        icon: 'pi pi-file',
        command: () => this.exportAnomalies('csv'),
      },
    ];

    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const tab = params.get('tab');
      const focusId = params.get('focusAnomalyId');
      const bidNumber = readLinkedBidNumberFromQuery(params);
      if (tab === 'map') {
        this.viewTab = 'map';
        this.cdr.markForCheck();
      }
      if (bidNumber !== this.lastQueryLinkedBidNumber) {
        this.lastQueryLinkedBidNumber = bidNumber;
        this.filters = {...this.filters, LinkedBidNumber: bidNumber};
        void this.rebuildFiltersParams();
      }
      if (focusId && tab === 'map') {
        this.pendingFocusAnomalyId = focusId;
        this.tryFocusAnomalyFromQuery(0);
      }
    });
  }

  /** Deep link: `/crop-monitoring/anomalies-map?tab=map&focusAnomalyId=...` (e.g. from request edit). */
  private tryFocusAnomalyFromQuery(attempt: number): void {
    const id = this.pendingFocusAnomalyId;
    if (!id || this.viewTab !== 'map') {
      return;
    }

    setTimeout(() => {
      const gis = this.gisAnomalies;
      if (!gis?.isMapReady()) {
        if (attempt < 40) {
          this.tryFocusAnomalyFromQuery(attempt + 1);
        }
        return;
      }

      this.anomalyPolygonsService
        .satelliteAnomalyPolygonsGetByIdIdGet(id)
        .pipe(takeUntil(this.destroy$), take(1))
        .subscribe({
        next: (ap) => {
          gis.focusAnomalyRow({anomalyPolygon: ap});
          this.pendingFocusAnomalyId = null;
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {tab: 'map', focusAnomalyId: null},
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        },
        error: () => {
          this.pendingFocusAnomalyId = null;
        },
      });
    }, attempt === 0 ? 0 : 120);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private toUtcMidnightIso(date: Date): string {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())).toISOString();
  }

  /** Worker origin already means GroundSurvey — product source (Custom/S2/Landsat) does not apply. */
  isProductSourceFilterDisabled(): boolean {
    return this.filters?.['AnomalyOrigin'] === 'Worker';
  }

  onFiltersModelChange(model: Record<string, unknown> | null | undefined): void {
    this.filters = model ?? {};
    if (this.isProductSourceFilterDisabled() && this.filters['productSourceType'] != null) {
      this.filters = {...this.filters, productSourceType: null};
    }
  }

  async applyFilters(): Promise<void> {
    if (this.isProductSourceFilterDisabled()) {
      this.filters = {...this.filters, productSourceType: null};
    }
    await this.rebuildFiltersParams();
    this.writeLinkedBidNumberQuery(parseLinkedBidNumber(this.filters['LinkedBidNumber']));
  }

  clearFilters(): void {
    this.filters = {AnomalyOrigin: 'Satellite'};
    this.filtersParams = withDefaultAnomalyArchiveFilter([
      {
        filterField: 'AnomalyOrigin',
        filterOperator: FilterOperator.Equals as any,
        filterValue: 'Satellite',
      },
    ]);
    this.toggleFilter = !this.toggleFilter;
    this.writeLinkedBidNumberQuery(null);
  }

  private async rebuildFiltersParams(): Promise<void> {
    this.loading = true;
    this.cdr.markForCheck();
    try {
      this.filtersParams = await this.buildFiltersParams();
      this.toggleFilter = !this.toggleFilter;
    } finally {
      this.loading = false;
      this.cdr.markForCheck();
    }
  }

  /**
   * LinkedBidNumber is list-only. Resolve bid {@code number} → AnomalyPolygon {@code Id}/{@code BidId}.
   */
  private resolveLinkedBidAnomalyFilter(bidNumber: number) {
    return this.bidService.cropMonitoringBidGetByQueryPost(createBidNumberEqualsQuery(bidNumber)).pipe(
      switchMap(bids => {
        const bid = pickFirstBid(bids);
        const bidId = String(bid?.id ?? '').trim();
        if (!bidId) {
          return of(emptyAnomalyMatchFilter());
        }
        return this.anomalyPolygonsService
          .satelliteAnomalyPolygonsCustomGetAnomalyPolygonsPost(createPolygonsByBidIdQuery(bidId))
          .pipe(
            map(rows =>
              anomalyPolygonFilterForLinkedBid({
                bidId,
                anomalyIds: collectLinkedAnomalyIds(bid ?? {}, anomalyIdsFromCustomGetRows(rows)),
              }),
            ),
            catchError(() =>
              of(
                anomalyPolygonFilterForLinkedBid({
                  bidId,
                  anomalyIds: collectLinkedAnomalyIds(bid ?? {}, []),
                }),
              ),
            ),
          );
      }),
      catchError(() => of(emptyAnomalyMatchFilter())),
    );
  }

  private writeLinkedBidNumberQuery(bidNumber: number | null): void {
    if (bidNumber === this.lastQueryLinkedBidNumber) {
      return;
    }
    this.lastQueryLinkedBidNumber = bidNumber;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {[ANOMALIES_MAP_BID_NUMBER_QUERY_KEY]: bidNumber},
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private async buildFiltersParams(): Promise<FieldFilter[]> {
    const raw = {...this.filters};
    const range = raw['originalDate'] as unknown[] | undefined;
    delete raw['originalDate'];
    const bidNumber = parseLinkedBidNumber(raw['LinkedBidNumber']);
    delete raw['LinkedBidNumber'];
    if (raw['AnomalyOrigin'] === 'Worker') {
      delete raw['productSourceType'];
    }

    const computed = buildFilterQuery(raw as Record<string, Object>) as FieldFilter[];
    if (bidNumber != null) {
      computed.push(
        await firstValueFrom(
          this.resolveLinkedBidAnomalyFilter(bidNumber).pipe(takeUntil(this.destroy$)),
        ),
      );
    }

    for (const f of computed) {
      if (
        (f.filterField === 'productSourceType' || f.filterField === 'anomalyType') &&
        f.filterValue !== null &&
        f.filterValue !== undefined &&
        f.filterValue !== ''
      ) {
        const num = Number(f.filterValue);
        if (Number.isFinite(num)) {
          (f as {filterValue?: unknown}).filterValue = num;
        }
      }
    }

    const hasStart = range && range[0];
    const hasEnd = range && range[1];
    if (hasStart) {
      replaceFilter(
        'originalDate',
        {
          filterField: 'originalDate',
          filterOperator: FilterOperator.GreaterThanOrEqual,
          filterValue: this.toUtcMidnightIso(new Date(range![0] as string | Date)),
        },
        computed,
      );
      if (hasEnd) {
        computed.push({
          filterField: 'originalDate',
          filterOperator: FilterOperator.LessThanOrEqual,
          filterValue: this.toUtcMidnightIso(new Date(range![1] as string | Date)),
        });
      }
    }

    await this.regionManagerService.includeChildInRequest(this.filters, computed as any);
    return withDefaultAnomalyArchiveFilter(computed);
  }

  openFilterModal(): void {
    this.isFilterModalOpen = true;
  }

  onListZoomToMap(row: any): void {
    this.viewTab = 'map';
    setTimeout(() => this.gisAnomalies?.focusAnomalyRow(row), 150);
  }

  async exportAnomalies(format: 'excel' | 'csv'): Promise<void> {
    const query = {filters: await this.buildFiltersParams()};
    this.exportLoading = true;
    const req$ =
      format === 'excel'
        ? this.anomalyPolygonsService.satelliteAnomalyPolygonsExportToExcelPost(
            query as any,
            'body',
            false,
            {httpHeaderAccept: 'application/octet-stream' as any},
          )
        : this.anomalyPolygonsService.satelliteAnomalyPolygonsExportFromCSVPost(
            query as any,
            'body',
            false,
            {httpHeaderAccept: 'application/octet-stream' as any},
          );

    req$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.exportLoading = false)),
      )
      .subscribe({
        next: result => {
          if (result instanceof Blob) {
            this.downloadExportBlob(result, format);
            return;
          }
          if (typeof result === 'string' && result.length > 0) {
            const mime =
              format === 'excel'
                ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                : 'text/csv;charset=utf-8;';
            this.downloadExportBlob(new Blob([result], {type: mime}), format);
          }
        },
      });
  }

  private downloadExportBlob(blob: Blob, format: 'excel' | 'csv'): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `anomalies-export-${new Date().toISOString().slice(0, 10)}.${format === 'excel' ? 'xlsx' : 'csv'}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
}
